import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Security (issue 2 HIGH, stage 2): the refresh token lives ONLY in the
// HttpOnly refresh cookie the backend sets. JavaScript (so any XSS) can no
// longer read it: the client never stores it in localStorage and the
// login/refresh response body no longer carries it. withCredentials is
// required for the browser to include the cookie on a cross-origin XHR to
// the API. The short-lived access token still lives in localStorage because
// a bearer header has to be set from JavaScript; only the long-lived
// refresh token is moved out of reach.
const client = axios.create({ baseURL: API, withCredentials: true });

// ---- Session token helpers ------------------------------------------
export const getAccess = () => localStorage.getItem("klareet.access");

// A non-secret marker that a session exists, so a logged-out visitor does
// not fire a pointless refresh. This is NOT the refresh token (that is in
// the HttpOnly cookie); reading it grants an attacker nothing. It is kept
// in sync with the cookie by login, refresh, and logout.
const SESSION_FLAG = "klareet.session";
const markSession = () => {
  try { localStorage.setItem(SESSION_FLAG, "1"); } catch (_e) { /* ignore */ }
};
export const hasSession = () => {
  try {
    return !!(localStorage.getItem(SESSION_FLAG) || localStorage.getItem("klareet.access"));
  } catch (_e) {
    return false;
  }
};

// Stage 2 migration: a stage-1 client may still hold a refresh token in
// localStorage. Its cookie is already set (stage 1 set it on every login
// and refresh), so convert the stored token to the non-secret marker and
// scrub the token itself, once, on load.
try {
  if (localStorage.getItem("klareet.refresh")) {
    markSession();
    localStorage.removeItem("klareet.refresh");
  }
} catch (_e) { /* ignore */ }

export function setTokens(access, _refresh) {
  // Stage 2: only the short-lived access token is stored. The refresh token
  // is never written to localStorage; it rides in the HttpOnly cookie. A
  // successful auth returns an access token, so that is our signal to keep
  // the non-secret session marker for the refresh guard.
  if (access) {
    localStorage.setItem("klareet.access", access);
    markSession();
  }
}

export function clearSession() {
  localStorage.removeItem("klareet.access");
  localStorage.removeItem("klareet.refresh"); // legacy scrub, in case one lingers
  localStorage.removeItem(SESSION_FLAG);
  // Clear the cached read too, so a shared browser never carries one
  // person's assessment state (or the KLA-009 unlock) into the next
  // person's session.
  localStorage.removeItem("klareet.report");
}

function redirectToLogin() {
  const p = window.location.pathname || "/";
  const dest = p.startsWith("/grow") ? "/grow" : "/";
  if (p !== dest && p !== "/c" && !p.startsWith("/share/")) window.location.assign(dest);
}

// Refresh the access token. The refresh token rides in the HttpOnly cookie
// (sent by withCredentials), so the request body is empty. The backend
// rotates the cookie and returns a fresh access token; nothing sensitive is
// stored client side.
export async function refreshSession() {
  try {
    const { data } = await axios.post(
      `${API}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch (_e) {
    return false;
  }
}

// Sign out cleanly: revoke the refresh token server side (read from the
// HttpOnly cookie) and clear the cookie, then clear local state.
export async function endSession() {
  try {
    await axios.post(
      `${API}/auth/logout`,
      {},
      { withCredentials: true },
    );
  } catch (_e) { /* best effort */ }
  clearSession();
}

client.interceptors.request.use((config) => {
  const token = getAccess();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- Silent refresh on 401 ------------------------------------------
// A single in-flight refresh serves every request that 401s while it
// runs, so we never fire two refreshes at once (which would revoke the
// rotated token out from under a sibling request).
let isRefreshing = false;
let waiters = [];
const flushWaiters = (err, token) => {
  waiters.forEach(({ resolve, reject }) => (err ? reject(err) : resolve(token)));
  waiters = [];
};

client.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthPath =
      url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/register");

    if (status !== 401 || original?._retry || isAuthPath) {
      return Promise.reject(error);
    }
    if (!hasSession()) {
      clearSession();
      redirectToLogin();
      return Promise.reject(error);
    }

    original._retry = true;
    if (isRefreshing) {
      return new Promise((resolve, reject) => waiters.push({ resolve, reject })).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return client(original);
      });
    }

    isRefreshing = true;
    try {
      const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      setTokens(data.access_token, data.refresh_token);
      flushWaiters(null, data.access_token);
      original.headers.Authorization = `Bearer ${data.access_token}`;
      return client(original);
    } catch (e) {
      flushWaiters(e, null);
      clearSession();
      redirectToLogin();
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  },
);

export default client;

// ---- SSE streaming (POST + bearer) ----------------------------------
// EventSource cannot send an Authorization header or a POST body, so we
// stream Klair's line with fetch and parse the `data:` frames ourselves.
// On a 401 we refresh once and retry, mirroring the axios interceptor.
// Handlers: { onShell(turn), onToken(text), onDone(turn), onError(err) }.
// Resolves true on a clean stream, false on any failure so the caller can
// fall back to the non-streaming endpoint.
export async function streamSSE(path, body, handlers = {}, _retried = false) {
  const { onShell, onToken, onDone, onError } = handlers;
  try {
    const token = getAccess();
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body || {}),
    });
    if (res.status === 401 && !_retried && hasSession()) {
      const ok = await refreshSession();
      if (ok) return streamSSE(path, body, handlers, true);
    }
    if (!res.ok || !res.body) {
      onError && onError(new Error(`stream ${res.status}`));
      return false;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep;
      while ((sep = buf.indexOf("\n\n")) >= 0) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        const line = frame.replace(/^data:\s?/, "").trim();
        if (!line) continue;
        let ev;
        try { ev = JSON.parse(line); } catch (_e) { continue; }
        if (ev.type === "shell") onShell && onShell(ev.turn || {});
        else if (ev.type === "token") onToken && onToken(ev.text || "");
        else if (ev.type === "done") onDone && onDone(ev.turn || {});
        else if (ev.type === "error") {
          onError && onError(new Error(ev.detail || "stream error"));
          return false;
        }
      }
    }
    return true;
  } catch (e) {
    onError && onError(e);
    return false;
  }
}

/**
 * Host and routing plan
 *   start.klareet.com               serves KlareetStart
 *   grow.klareet.com                serves KlareetGrow
 *   klareet.com/<org-slug>          co-branded institution or business entry
 *   klareet.com                     marketing root
 *   api.klareet.com                 API surface
 * The super-admin console lives at a long unguessable path served
 * from the backend directly. It is not reachable through a
 * subdomain.
 */
export const detectProduct = () => {
  const host = window.location.hostname || "";
  if (host.startsWith("start.")) return "start";
  if (host.startsWith("grow.")) return "grow";
  const path = window.location.pathname || "";
  if (path.startsWith("/grow")) return "grow";
  return "start";
};

// Read a co-brand slug from the URL. Supported patterns:
//   klareet.com/c/<slug>            explicit co-brand path
//   klareet.com/<slug>              legacy path form
//   any host with ?partner=<slug>   query string
//   any host with ?org=<slug>       query string
// On start./grow. subdomains the path form is not used because
// reserved app routes live there; the query string still works.
export const detectOrgSlug = () => {
  const host = window.location.hostname || "";
  const path = window.location.pathname || "";
  const search = new URLSearchParams(window.location.search || "");
  const q = search.get("partner") || search.get("org");
  if (q) {
    try { localStorage.setItem("klareet.org.slug", q); } catch (_e) { /* ignore */ }
    return q;
  }
  const parts = path.split("/").filter(Boolean);
  if (parts[0] === "c" && parts[1]) {
    try { localStorage.setItem("klareet.org.slug", parts[1]); } catch (_e) { /* ignore */ }
    return parts[1];
  }
  if (host.startsWith("start.") || host.startsWith("grow.")) {
    try { return localStorage.getItem("klareet.org.slug") || null; } catch (_e) { return null; }
  }
  const first = parts[0];
  if (!first) {
    try { return localStorage.getItem("klareet.org.slug") || null; } catch (_e) { return null; }
  }
  // Reserved app routes are not slugs.
  const reserved = new Set([
    "consent", "klair", "assess", "report", "grow",
    "welcome", "onboarding", "home", "career", "roadmap",
    "learning", "resume", "ask", "building", "c", "checkin",
    "account", "preferences", "whats-new", "upcoming", "support", "help", "dashboard", "college",
  ]);
  if (reserved.has(first)) {
    try { return localStorage.getItem("klareet.org.slug") || null; } catch (_e) { return null; }
  }
  try { localStorage.setItem("klareet.org.slug", first); } catch (_e) { /* ignore */ }
  return first;
};

// Fetch co-brand tokens by slug. Returns null on 404 so the caller
// can fall back to the default Klareet chrome.
export const fetchCobrand = async (slug) => {
  try {
    const r = await client.get(`/orgs/${encodeURIComponent(slug)}`);
    return r.data;
  } catch (e) {
    return null;
  }
};

// Development only: fetch the list of demo personas. Returns null if
// the endpoint is not enabled on the backend, which is expected in
// production.
export const fetchDemoPersonas = async () => {
  try {
    const r = await client.get("/dev/demo-personas");
    return r.data;
  } catch (_e) {
    return null;
  }
};

// Development only: log in as a persona. Persists the token pair and
// the report if the persona ships with one.
export const demoLogin = async (key) => {
  const r = await client.post("/dev/demo-login", { key });
  const d = r.data || {};
  if (d.access_token)  localStorage.setItem("klareet.access", d.access_token);
  if (d.access_token)  markSession();
  if (d.report)        localStorage.setItem("klareet.report", JSON.stringify(d.report));
  if (d.org_slug)      { try { localStorage.setItem("klareet.org.slug", d.org_slug); } catch (_e) { /* ignore */ } }
  return d;
};

// Fetch the person's profile photo as a blob object URL, or null when
// there is none. The caller owns the URL and should revoke it on
// unmount. An <img> cannot send the auth header, so we fetch as a blob.
export const fetchProfilePhotoUrl = async () => {
  try {
    const r = await client.get("/profile/photo", { responseType: "blob" });
    return URL.createObjectURL(r.data);
  } catch (_e) {
    return null;
  }
};

// Normalise an axios error into a human-readable string. FastAPI returns
// 422 validation errors as an array of objects ({type, loc, msg, ...}).
// Rendering that array directly crashed React (error #31) and blanked the
// whole page. Always resolve to a string.
export function errText(e, fallback = "Something went wrong. Please try again.") {
  const d = e && e.response && e.response.data ? e.response.data.detail : undefined;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const msgs = d
      .map((x) => (x && typeof x === "object" ? (x.msg || "") : String(x)))
      .filter(Boolean);
    if (msgs.length) return msgs.join(". ");
  }
  const m = e && e.response && e.response.data ? e.response.data.message : undefined;
  if (typeof m === "string") return m;
  if (e && typeof e.message === "string") return e.message;
  return fallback;
}
